/**
 * NunbaChatProvider - Context + state for the floating Nunba chat widget.
 *
 * Wires to the EXISTING chatApi (Flask :5000 /chat) and useTTS hook.
 * Does NOT import Demopage — lightweight direct API calls only.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { chatApi } from '../../../../services/socialApi';
import { useTTS } from '../../../../hooks/useTTS';
import { useSocial } from '../../../../contexts/SocialContext';
import { classifyError, getBackoff, makeMsgId } from '../../../../utils/chatRetry';

const NunbaChatContext = createContext(null);

export function useNunbaChat() {
  const ctx = useContext(NunbaChatContext);
  if (!ctx) throw new Error('useNunbaChat must be inside NunbaChatProvider');
  return ctx;
}

/* ── Diverse avatar palette — seeded by agent name ── */
const AVATAR_PALETTES = [
  { bg: '#8B5E3C', accent: '#FFD8B1' }, // warm brown
  { bg: '#6C63FF', accent: '#C5C1FF' }, // aspiration violet
  { bg: '#D4A373', accent: '#FEFAE0' }, // golden tan
  { bg: '#2D6A4F', accent: '#B7E4C7' }, // forest green
  { bg: '#E76F51', accent: '#FFDDD2' }, // terracotta
  { bg: '#264653', accent: '#A8DADC' }, // deep teal
  { bg: '#F4A261', accent: '#FFF1DB' }, // amber
  { bg: '#7B2CBF', accent: '#E0AAFF' }, // royal purple
  { bg: '#D62828', accent: '#FFCCD5' }, // crimson
  { bg: '#023E8A', accent: '#90E0EF' }, // navy
  { bg: '#BC6C25', accent: '#DDA15E' }, // sienna
  { bg: '#606C38', accent: '#FEFAE0' }, // olive
];

/** Deterministic palette from agent name/id */
export function getAgentPalette(seed) {
  if (!seed) return AVATAR_PALETTES[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
}

const STORAGE_KEY = (agentId) => `nunba_chat_${agentId || 'default'}`;
const MAX_STORED = 50;

function loadMessages(agentId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(agentId));
    if (!raw) return [];
    const msgs = JSON.parse(raw);
    // Clean up stale in-flight statuses from previous sessions
    return msgs.map(m =>
      (m.status === 'sending' || m.status === 'retrying')
        ? { ...m, status: 'failed', error: 'Interrupted — tap to retry' }
        : m
    );
  } catch { return []; }
}
function saveMessages(agentId, msgs) {
  try {
    localStorage.setItem(STORAGE_KEY(agentId), JSON.stringify(msgs.slice(-MAX_STORED)));
  } catch { /* quota */ }
}

export default function NunbaChatProvider({ children }) {
  const { currentUser } = useSocial();
  const userId = currentUser?.id || localStorage.getItem('hevolve_access_id') || '1';

  // Core state
  const [isExpanded, setIsExpanded] = useState(false);
  // Starts at the value the CAPTURE had, not the visitor's.
  //
  // A lazy initializer still runs during the first render, so reading storage
  // here made the pill present in the prerendered file and absent on the first
  // render of anyone who had dismissed it -- a whole subtree the file has and
  // React does not, which is #418, and enough to unravel hydration for the
  // page around it. This provider wraps every /social route, so it was the
  // second of the two storage reads in that shell.
  //
  // No seed here, unlike the guest chip in SocialLayout: useSeededValue owns
  // the value it returns and hands back no setter, and dismiss()/undismiss()
  // need one. Same shape either way -- reproduce the file first, correct on
  // mount -- so the effect below is that correction, one frame later and after
  // the comparison React does at hydration.
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem('nunba_chat_dismissed') === 'true') setIsDismissed(true);
    } catch { /* storage disabled: showing the pill is the safe default */ }
  }, []);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null); // { prompt_id, name }
  const [availableAgents, setAvailableAgents] = useState([]);
  const [ttsEnabled, setTtsEnabled] = useState(false);

  const conversationIdRef = useRef(uuidv4());
  const currentAgentRef = useRef(null); // race-condition guard

  // TTS — wired to existing hook
  const tts = useTTS({ enabled: ttsEnabled, autoSpeak: false });

  // switchAgent must be defined BEFORE useEffects that reference it (TDZ fix)
  const switchAgent = useCallback((agent) => {
    tts.stop();
    setCurrentAgent(agent);
  }, [tts]);

  // Load agents on mount
  useEffect(() => {
    chatApi.getPrompts(userId).then((res) => {
      const data = res.data || res || {};
      const prompts = Array.isArray(data.prompts) ? data.prompts : (Array.isArray(data) ? data : []);
      setAvailableAgents(prompts.slice(0, 8)); // widget limit
    }).catch(() => { /* backend not up */ });
  }, [userId]);

  // Listen for external agent selection (e.g., "Chat with HART" buttons)
  useEffect(() => {
    const handler = (e) => {
      const { agentId, agentName } = e.detail || {};
      if (!agentId) return;
      // Find agent in available list or create a transient entry
      const found = availableAgents.find((a) => String(a.prompt_id) === String(agentId));
      if (found) {
        switchAgent(found);
      } else {
        switchAgent({ prompt_id: agentId, name: agentName || `HART ${agentId}` });
      }
      setIsExpanded(true);
    };
    window.addEventListener('nunba:selectAgent', handler);
    return () => window.removeEventListener('nunba:selectAgent', handler);
  }, [availableAgents, switchAgent, setIsExpanded]);

  // Load messages when agent changes
  useEffect(() => {
    const agentKey = currentAgent?.prompt_id || 'default';
    setMessages(loadMessages(agentKey));
    conversationIdRef.current = uuidv4();
    currentAgentRef.current = agentKey;
  }, [currentAgent]);

  // Persist messages on change
  useEffect(() => {
    const agentKey = currentAgent?.prompt_id || 'default';
    if (messages.length > 0) saveMessages(agentKey, messages);
  }, [messages, currentAgent]);

  // Dismiss persistence
  const dismiss = useCallback(() => {
    setIsDismissed(true);
    setIsExpanded(false);
    try { localStorage.setItem('nunba_chat_dismissed', 'true'); } catch (err) { console.error('localStorage dismiss failed:', err); }
  }, []);

  const undismiss = useCallback(() => {
    setIsDismissed(false);
    try { localStorage.removeItem('nunba_chat_dismissed'); } catch (err) { console.error('localStorage undismiss failed:', err); }
  }, []);

  // ── Retry: message status updater ──
  const updateMsgById = useCallback((messageId, updates) => {
    setMessages(prev => prev.map(m => m.messageId === messageId ? { ...m, ...updates } : m));
  }, []);

  /** Parse @mentions from input text. Returns { cleanText, mentionedAgents[] } */
  const parseMentions = useCallback((text) => {
    const mentionRegex = /@(\S+)/g;
    const mentionedAgents = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const name = match[1].toLowerCase();
      const agent = availableAgents.find((a) =>
        (a.name || '').toLowerCase() === name ||
        (a.name || '').toLowerCase().replace(/\s+/g, '.') === name
      );
      if (agent && !mentionedAgents.find(m => m.prompt_id === agent.prompt_id)) {
        mentionedAgents.push(agent);
      }
    }
    return { mentionedAgents };
  }, [availableAgents]);

  // Send message with persistent retry + @mention multi-HART support
  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;

    const { mentionedAgents } = parseMentions(text);
    const agentKey = currentAgentRef.current;
    const msgId = makeMsgId();
    const userMsg = { role: 'user', text: text.trim(), ts: Date.now(), messageId: msgId, status: 'sending',
      ...(mentionedAgents.length > 0 ? { mentions: mentionedAgents.map(a => a.name || a.prompt_id) } : {}) };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);
    setIsTyping(true);

    let retryCount = 0;
    let lastReason = '';

    while (true) {
      if (currentAgentRef.current !== agentKey) return; // agent switched

      if (retryCount > 0) {
        const backoff = getBackoff(retryCount - 1);
        updateMsgById(msgId, {
          status: 'retrying',
          error: `${lastReason} — retrying in ${Math.round(backoff / 1000)}s...`,
          retryCount,
        });
        setIsTyping(false); // no dots during backoff
        await new Promise(r => setTimeout(r, backoff));
        if (currentAgentRef.current !== agentKey) return;
        setIsTyping(true);
      }

      try {
        updateMsgById(msgId, { status: 'sending', error: null });
        const _promptId = currentAgent?.prompt_id || null;
        const _needsTools = !!(_promptId || currentAgent?.create_agent || currentAgent?.autonomous_creation);
        const res = await chatApi.chat({
          // The /chat backend reads the message from `prompt` or
          // `input_text` (hart_intelligence_entry: `data.get('prompt') or
          // data.get('input_text')`) and never `text`. Sending only `text`
          // meant this panel posted an empty prompt and got back
          // "prompt is required" -- the social-area chat was dead. Verified
          // against a live /chat call: with input_text set, the agent
          // replies. `text` is kept for any consumer that still reads it.
          text: text.trim(),
          input_text: text.trim(),
          user_id: userId,
          agent_id: _promptId || 'local_assistant',
          agent_type: 'local',
          conversation_id: conversationIdRef.current,
          video_req: false,
          prompt_id: _promptId,
          create_agent: false,
          autonomous_creation: false,
          casual_conv: !_needsTools,
          ...(mentionedAgents.length > 0 ? { mentioned_agents: mentionedAgents.map(a => a.prompt_id) } : {}),
        });

        if (currentAgentRef.current !== agentKey) return;

        updateMsgById(msgId, { status: 'sent', error: null, retryCount: undefined });

        const data = res.data || res || {};
        const reply = data.text || data.response || 'I heard you, but I have nothing to add right now.';
        const agentStatus = data.Agent_status || data.agent_status || null;
        const respondingAgent = data.responding_agent || currentAgent?.name || null;

        setMessages((prev) => [...prev, {
          role: 'assistant', text: reply, ts: Date.now(),
          agentStatus, postId: data.post_id || null,
          agentName: respondingAgent,
        }]);

        if (ttsEnabled && tts.isAvailable) tts.speak(reply);

        setIsLoading(false);
        setIsTyping(false);
        return; // success
      } catch (err) {
        if (currentAgentRef.current !== agentKey) return;
        const { reason, retryable } = classifyError(err);
        lastReason = reason;

        if (!retryable) {
          updateMsgById(msgId, { status: 'failed', error: reason });
          setIsLoading(false);
          setIsTyping(false);
          return;
        }

        retryCount++;
        // Loop continues with backoff...
      }
    }
  }, [userId, currentAgent, isLoading, ttsEnabled, tts, updateMsgById, parseMentions]);

  // Manual retry for failed messages
  const retryMessage = useCallback((messageId) => {
    const msg = messages.find(m => m.messageId === messageId);
    if (!msg || msg.status !== 'failed' || isLoading) return;
    setMessages(prev => prev.filter(m => m.messageId !== messageId));
    sendMessage(msg.text);
  }, [messages, isLoading, sendMessage]);

  // Delete a stuck/retrying message
  const deleteMessage = useCallback((messageId) => {
    setMessages(prev => prev.filter(m => m.messageId !== messageId));
  }, []);

  const clearMessages = useCallback(() => {
    const agentKey = currentAgent?.prompt_id || 'default';
    setMessages([]);
    conversationIdRef.current = uuidv4();
    try { localStorage.removeItem(STORAGE_KEY(agentKey)); } catch (err) { console.error('localStorage clearMessages failed:', err); }
  }, [currentAgent]);

  const value = {
    isExpanded, setIsExpanded,
    isDismissed, dismiss, undismiss,
    messages, isLoading, isTyping,
    currentAgent, availableAgents,
    sendMessage, switchAgent, clearMessages,
    retryMessage, deleteMessage,
    ttsEnabled, setTtsEnabled, tts,
    getAgentPalette,
  };

  return (
    <NunbaChatContext.Provider value={value}>
      {children}
    </NunbaChatContext.Provider>
  );
}
